import { useCallback, useState } from "react";
import { useLocation } from "wouter";

import { ArtifactLibraryBody } from "@/components/artifactLibrary/ArtifactLibraryBody.tsx";
import { getThreadIdFromPath } from "@/components/threads/util.ts";
import {
  ARTIFACT_LIBRARY_CHIPS,
  type ArtifactLibraryChipId,
  type ArtifactLibrarySharingId,
  DEFAULT_LIBRARY_SORT,
  type LibrarySort,
} from "@/types/artifactLibrary.ts";
import {
  appliedFilterLabel,
  librarySortAxis,
  type PanelControlAxes,
  PanelControls,
  type PanelViewMode,
  sharingAxis,
  viewAxis,
} from "./Settings/panelControls.tsx";
import { panelDescriptionClass, SettingsPanelHeader } from "./Settings/SettingsPanelHeader.tsx";
import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";
import type { ReactNode } from "react";

const isChipId = (value: string): value is ArtifactLibraryChipId =>
  ARTIFACT_LIBRARY_CHIPS.some((chip) => chip.id === value);

/**
 * Search's own search box and its type / sharing / view axes, owned in one place
 * so the settings panel and the canvas view can't drift into offering different
 * ones.
 *
 * Returns the same `controls` object both the menu and the header's width
 * measurement read, so those two can't be computed from different axes, plus the
 * `controls` prop `ArtifactLibraryBody` takes to render results only.
 */
export const useLibraryControls = () => {
  const [searchInput, setSearchInput] = useState("");
  const [activeChipId, setActiveChipId] = useState<ArtifactLibraryChipId>("all");
  // Rows by default: the Library is read down a column, and its cards earn
  // their space only when someone goes looking for them.
  const [viewMode, setViewMode] = useState<PanelViewMode>("list");
  const [sharingId, setSharingId] = useState<ArtifactLibrarySharingId>("all");
  // Relevance by default: a search is a question, and the best answer leads.
  // Date is the deliberate choice to see what is newest regardless of fit.
  const [sort, setSort] = useState<LibrarySort>(DEFAULT_LIBRARY_SORT);

  const controls: PanelControlAxes = {
    filters: [
      {
        label: "Type",
        value: activeChipId,
        onChange: (value: string) => {
          if (isChipId(value)) setActiveChipId(value);
        },
        options: ARTIFACT_LIBRARY_CHIPS.map((chip) => ({ value: chip.id, label: chip.label })),
        defaultValue: "all",
        display: "segmented",
        statsigName: "artifact-library-type",
      },
      sharingAxis({ value: sharingId, onChange: setSharingId }),
    ],
    sort: librarySortAxis({ value: sort, onChange: setSort }),
    view: viewAxis({
      value: viewMode,
      onChange: setViewMode,
      defaultValue: "list",
      statsigName: "artifact-library-view",
    }),
  };

  // The hint names what the field will actually search. The type filter is set
  // on a different control, so a hint still promising "all content" while a chip
  // narrows the results describes a search the page is no longer doing.
  const activeChip = ARTIFACT_LIBRARY_CHIPS.find((chip) => chip.id === activeChipId);
  const placeholder =
    !activeChip || activeChip.id === "all"
      ? "Search all content"
      : `Search ${activeChip.label.toLowerCase()}`;

  return {
    controls,
    bodyControls: {
      searchInput,
      activeChipId,
      viewMode,
      sort,
      sharingId,
    },
    search: {
      value: searchInput,
      onChange: setSearchInput,
      placeholder,
      statsigName: "artifact-library-search",
    },
  };
};

/**
 * The Artifact Library, rendered as a Settings tab. The header row carries the
 * toolkit — search, the type filter (the chip catalog as a dropdown), and the
 * view dropdown — while `ArtifactLibraryBody` renders only the results. The
 * other body hosts (search dialog, standalone page) keep the body's own
 * controls, segmented toggle included.
 */
export const LibraryPanel = () => {
  const { controls, bodyControls, search } = useLibraryControls();

  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title="Search"
        search={search}
        filters={
          <PanelControls
            labeled
            {...controls}
            subject="library"
            statsigName="artifact-library-filter"
          />
        }
        mobileFilters={
          <PanelControls {...controls} subject="library" statsigName="artifact-library-filter" />
        }
        toolsKey={appliedFilterLabel(controls)}
      />
      <p className={panelDescriptionClass}>Everything Okthx has created and saved for you.</p>
      <ArtifactLibraryBody
        controls={bodyControls}
        // pt-0: the header row's mb-6 already provides the gap; the body's own
        // top padding stacked a second one on top of it.
        className="pt-0"
      />
    </div>
  );
};

/**
 * The Library on the canvas: everything the assistant has made, with the same
 * search, type filter, and card/list switch the settings panel offers.
 *
 * `ArtifactLibraryBody` was already built to be hosted — pass it `controls` and
 * it renders results only, leaving the search box, chip row, and view toggle to
 * whoever is showing it. So this needed no extraction of its own; only the axes
 * came out, into `useLibraryControls`, so the two hosts read one definition.
 */
export const LibraryCanvasView = ({
  accountSwitcher,
  leading,
  onSelectThread,
}: {
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
  /**
   * Where a thread result goes. The host passes the same opener its thread list
   * uses, so picking a thread here lands exactly where picking one there does —
   * with Search named in the header as the way back, which a bare navigation to
   * the same URL would not carry.
   */
  onSelectThread?: (threadId: number) => void;
}) => {
  const { controls, bodyControls, search } = useLibraryControls();
  const [, setLocation] = useLocation();

  // A result leads two ways now: a thread, or an artifact someone shared with
  // you — the body reports both as a path rather than the host guessing from an
  // id. A thread goes through the opener, which is what keeps the trail back to
  // Search; anything else is a plain navigation.
  const handleNavigate = useCallback(
    (path: string) => {
      const threadId = getThreadIdFromPath(path);
      if (threadId !== undefined && onSelectThread) {
        onSelectThread(threadId);
        return;
      }
      setLocation(path);
    },
    [onSelectThread, setLocation],
  );

  return (
    <CanvasPivotPanel
      title="Search"
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar
          description="Everything Okthx has created and saved for you."
          controls={controls}
          search={{ ...search, statsigName: "library-canvas-search" }}
          // The one screen that opens on its field rather than its axes: a
          // search is what brought you here, and the type row is how you narrow
          // what it returns afterwards.
          mobileFocus="search"
          subject="library"
          statsigName="library-canvas-filter"
        />
      }
    >
      {/* `pt-0`: the toolbar above supplies the gap down to the results — its
          own `mb-4`, or the description's bottom margin when one is showing —
          and the body's top padding would stack a second one under it. */}
      <ArtifactLibraryBody controls={bodyControls} className="pt-0" onNavigate={handleNavigate} />
    </CanvasPivotPanel>
  );
};
